<script setup>
import { computed, onMounted, ref, watch } from 'vue';

const props = defineProps({
    admins: {
        type: Array,
        default: () => [],
    },
    flashMessageKey: {
        type: String,
        default: null,
    },
    flashMessageArgs: {
        type: Object,
        default: () => ({}),
    },
    flashGeneratedPassword: {
        type: String,
        default: null,
    },
    flashAdmin: {
        type: Object,
        default: null,
    },
});

const policies = ref([]);
const accountCreationEnabled = ref(false);
const domain = ref(false);
const csrf = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

const adminList = ref([...props.admins]);

watch(
    () => props.admins,
    (value) => {
        adminList.value = [...value];
    }
);

if (props.flashAdmin) {
    const existingIndex = adminList.value.findIndex((admin) => admin.id === props.flashAdmin.id);
    if (existingIndex === -1) {
        adminList.value.push(props.flashAdmin);
    } else {
        adminList.value.splice(existingIndex, 1, props.flashAdmin);
    }
}

const formSuccess = ref(
    props.flashMessageKey ? _mft(props.flashMessageKey, props.flashMessageArgs ?? {}) : null
);
const generatedPassword = ref(props.flashGeneratedPassword ?? null);
const formError = ref(null);
const copyFeedback = ref(null);
const isSubmitting = ref(false);
const newAdminName = ref('');
const newAdminEmail = ref('');

const loadPolicies = async () => {
    const res = await axios.get(route('settings.policies.show'));

    accountCreationEnabled.value = res.data.accountCreationEnabled;
    policies.value = res.data.policies;
    domain.value = res.data.domain;
};

const createPolicy = async (type) => {
    const res = await axios.post(route('settings.policies.create'), {
        type,
    });
    if (res.data.success === true) {
        policies.value.push(res.data.policy);
    }
};

const resetFeedback = () => {
    formError.value = null;
    formSuccess.value = null;
    generatedPassword.value = null;
    copyFeedback.value = null;
};

const submitNewAdmin = async () => {
    resetFeedback();

    if (!newAdminName.value.trim() || !newAdminEmail.value.trim()) {
        formError.value = _mft('company:admin.error.missingFields');
        return;
    }

    isSubmitting.value = true;

    try {
        const response = await axios.post(route('settings.company.addAdmin'), {
            name: newAdminName.value.trim(),
            email: newAdminEmail.value.trim(),
        });

        const {
            message_key: messageKey,
            message_args: messageArgs,
            generated_password: password,
            admin,
        } = response.data;

        if (messageKey) {
            formSuccess.value = _mft(messageKey, messageArgs ?? {});
        }

        generatedPassword.value = password ?? null;

        if (admin) {
            const index = adminList.value.findIndex((existing) => existing.id === admin.id);
            if (index === -1) {
                adminList.value.push(admin);
            } else {
                adminList.value.splice(index, 1, admin);
            }
        }

        newAdminName.value = '';
        newAdminEmail.value = '';
    } catch (error) {
        if (error.response?.data?.message_key) {
            formError.value = _mft(
                error.response.data.message_key,
                error.response.data.message_args ?? {}
            );
        } else if (error.response?.data?.errors) {
            const messages = Object.values(error.response.data.errors).flat();
            formError.value = messages.join(' ');
        } else if (error.response?.data?.message) {
            formError.value = error.response.data.message;
        } else {
            formError.value = _mft('company:admin.error.generic');
        }
    } finally {
        isSubmitting.value = false;
    }
};

const copyGeneratedPassword = async () => {
    if (!generatedPassword.value) {
        return;
    }

    try {
        await navigator.clipboard.writeText(generatedPassword.value);
        copyFeedback.value = _mft('company:admin.passwordCopied');
    } catch (error) {
        copyFeedback.value = _mft('company:admin.copyPasswordFallback');
    }

    setTimeout(() => {
        copyFeedback.value = null;
    }, 2500);
};

onMounted(() => {
    loadPolicies();
});

const integrityPolicyExists = computed(() => {
    return policies.value.filter((policy) => policy.type === 'integrity-policy').length > 0;
});

const termsOfUsePolicyExists = computed(() => {
    return policies.value.filter((policy) => policy.type === 'terms-of-use').length > 0;
});

const integrityPolicy = computed(() => {
    return policies.value.filter((policy) => policy.type === 'integrity-policy')[0];
});

const termsOfUsePolicy = computed(() => {
    return policies.value.filter((policy) => policy.type === 'terms-of-use')[0];
});

const currentUserId = computed(() => window.myflow.user.id);
</script>

<template>
    <div class="mt-16 mb-4">
        <card-tabbed>
            <card-tabbed-tab
                :title="_mft('setting:policies.userAccounts.tabTitle')"
                value="user-accounts"
                :active="true"
            >
                <div class="grid md:grid-cols-2 gap-4 md:gap-8">
                    <div>
                        <h5 class="mb-4">
                            {{ _mft('setting:policies.toggleRegistration.title') }}
                        </h5>
                        <settings-account-creation-enabled
                            @update:content="accountCreationEnabled = $event"
                        />
                        <p
                            v-if="! accountCreationEnabled"
                            class="text-xs italic"
                        >
                            {{ _mft('setting:policies.toggleRegistration.requiredForFeatures') }}
                        </p>

                        <div v-if="integrityPolicyExists && termsOfUsePolicyExists && accountCreationEnabled">
                            <h5>
                                {{ _mft('setting:policies.linkToRegistration') }}
                            </h5>
                            <input
                                class="w-full max-w-full truncate bg-transparent focus:outline-none"
                                type="text"
                                :value="'https://'+domain+'/register'"
                                onfocus="event.target.select()"
                            >
                        </div>

                    </div>
                    <div class="space-y-8">
                        <div>
                            <h5>
                                {{ _mft('company:admin.currentAdmins')}}
                            </h5>
                            <ul class="divide-y">
                                <li
                                    v-for="admin in adminList"
                                    :key="admin.id"
                                    class="flex justify-between space-x-4 py-2"
                                >
                                    <div>
                                        <div>{{ admin.name }}</div>
                                        <div class="text-xs text-gray-400">{{ admin.email }}</div>
                                    </div>
                                    <a
                                        v-if="admin.id !== currentUserId"
                                        :href="route('settings.deleteAdmin', admin.id)"
                                    >
                                        <button-danger
                                            size="sm"
                                        >
                                            {{ _mft('shared:action.remove')}}
                                        </button-danger>
                                    </a>
                                </li>
                                <li
                                    v-if="adminList.length === 0"
                                    class="py-2 text-gray-400"
                                >
                                    {{ _mft('company:admin.noAdmins') }}
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h5 class="mb-4">
                                {{ _mft('company:admin.headline') }}
                            </h5>
                            <p>{{ _mft('company:admin.description') }}</p>

                            <success-banner v-if="formSuccess">
                                <template #header>
                                    {{ _mft('company:admin.successHeading') }}
                                </template>
                                <template #body>
                                    <p class="text-myflowGreen-900">{{ formSuccess }}</p>
                                    <div
                                        v-if="generatedPassword"
                                        class="mt-4 space-y-3 text-myflowGreen-900"
                                    >
                                        <p class="text-sm text-myflowGreen-900">
                                            {{ _mft('company:admin.generatedPasswordDescription') }}
                                        </p>
                                        <div class="flex flex-wrap items-center gap-3">
                                            <code class="px-3 py-2 font-mono text-sm bg-white border border-myflowGreen-400 rounded-md text-myflowGreen-900">
                                                {{ generatedPassword }}
                                            </code>
                                            <button-base
                                                style-type="secondary"
                                                size="sm"
                                                type="button"
                                                @click="copyGeneratedPassword"
                                            >
                                                {{ _mft('company:admin.copyPassword') }}
                                            </button-base>
                                        </div>
                                        <p
                                            v-if="copyFeedback"
                                            class="text-xs text-myflowGreen-900"
                                        >
                                            {{ copyFeedback }}
                                        </p>
                                        <p class="text-xs text-myflowGreen-900">
                                            {{ _mft('company:admin.generatedPasswordReminder') }}
                                        </p>
                                    </div>
                                    <p
                                        v-else
                                        class="mt-4 text-sm text-myflowGreen-900"
                                    >
                                        {{ _mft('company:admin.noPasswordGenerated') }}
                                    </p>
                                </template>
                            </success-banner>

                            <danger-banner v-if="formError">
                                <template #header>
                                    {{ _mft('company:admin.errorHeading') }}
                                </template>
                                <template #body>
                                    {{ formError }}
                                </template>
                            </danger-banner>

                            <form
                                :action="route('settings.company.addAdmin')"
                                method="POST"
                                class="space-y-4"
                                @submit.prevent="submitNewAdmin"
                            >
                                <input type="hidden" name="_token" :value="csrf">

                                <input-text
                                    name="name"
                                    :label="_mft('company:admin.form.nameLabel')"
                                    v-model:content="newAdminName"
                                    required
                                    placeholder="Elise Example"
                                />

                                <input-text
                                    name="email"
                                    inputtype="email"
                                    :label="_mft('company:admin.form.emailLabel')"
                                    v-model:content="newAdminEmail"
                                    required
                                    placeholder="elise@example.com"
                                />

                                <div class="flex items-center justify-start">
                                    <button-base
                                        style-type="success"
                                        type="submit"
                                        :state="isSubmitting ? 'waiting' : ''"
                                    >
                                        {{ isSubmitting ? _mft('shared:save.saving') : _mft('company:admin.form.submit') }}
                                    </button-base>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </card-tabbed-tab>

            <card-tabbed-tab
                value="integrity-policy"
                :title="_mft('auth:terms.integrityPolicy')"
            >
                <settings-policy
                    v-if="integrityPolicyExists"
                    :policy-type="integrityPolicy.type"
                />

                <div
                    v-else
                    class="flex justify-center py-8"
                >
                    <button-primary
                        @click="createPolicy('integrity-policy')"
                    >
                        {{ _mft('setting:policies.createPrivacyPolicy') }}
                    </button-primary>
                </div>
            </card-tabbed-tab>

            <card-tabbed-tab
                value="terms-of-use"
                :title="_mft('auth:terms.termsOfService')"
            >
                <settings-policy
                    v-if="termsOfUsePolicyExists"
                    :policy-type="termsOfUsePolicy.type"
                />

                <div
                    v-else
                    class="flex justify-center py-8"
                >
                    <button-primary
                        @click="createPolicy('terms-of-use')"
                    >
                        {{ _mft('setting:policies.createTermsOfUse') }}
                    </button-primary>
                </div>
            </card-tabbed-tab>
        </card-tabbed>
    </div>
</template>

<style scoped>

</style>
